import React from 'react';
import DecisionVoucherLayout from './components/layout/DecisionVoucherLayout';
import { useDecisionVoucherContext, useDecisionVoucherQualification } from './context';
import { DecisionVoucherV2Step, DecisionVoucherV2Type } from './models/decisionVoucherV2';
import useCurrentSav from '../../../../Hooks/useCurrentSav';

interface DecisionVoucherV2PageProps {
  savId: number;
}

/**
 * Composant principal pour la nouvelle interface de bon pour accord/refus
 */
const DecisionVoucherV2Page: React.FC<DecisionVoucherV2PageProps> = ({ savId }) => {
  // Récupération des données du SAV courant
  const { sav } = useCurrentSav();

  return (
    <DecisionVoucherLayout savId={savId}>
      <StepContentWrapper>
        {({ currentStep }) => (
          <StepContent step={currentStep} sav={sav} />
        )}
      </StepContentWrapper>
    </DecisionVoucherLayout>
  );
};

/**
 * Composant qui gère le contenu de l'étape actuelle
 * Ce composant est placé à l'intérieur du DecisionVoucherProvider
 */
interface StepContentProps {
  step: DecisionVoucherV2Step;
  sav: any | null;
}

const StepContent: React.FC<StepContentProps> = ({ step, sav }) => {
  // Ces hooks sont maintenant appelés à l'intérieur du DecisionVoucherProvider
  const { qualificationData, updateQualification } = useDecisionVoucherQualification();

  // // États locaux pour les données saisies pendant la qualification
  // const [currentKilometrage, setCurrentKilometrage] = useState<number | undefined>(
  //   qualificationData?.currentKilometrage
  // );

  // // Fonction de mise à jour du kilométrage
  // const handleKilometrageChange = (value: number) => {
  //   setCurrentKilometrage(value);
  //   updateQualification({ currentKilometrage: value });
  // };

  if (!sav) {
    return (
      <div className="tw-p-4 tw-bg-yellow-50 tw-text-yellow-700 tw-rounded-lg tw-border tw-border-yellow-200">
        <p>Impossible de charger les données du SAV. Veuillez réessayer.</p>
      </div>
    );
  }

  switch (step) {
    case DecisionVoucherV2Step.QUALIFICATION:
      return (
        <div className="tw-space-y-6">
          <p className="tw-text-gray-600">
            Examinez les informations du dossier pour déterminer l'éligibilité à la garantie.
            Utilisez les boutons en haut pour consulter les détails du contrat, du véhicule, du client et du devis.
          </p>

          {/* Questionnaire d'éligibilité */}
          <div className="tw-mt-8">
            <h3 className="tw-text-lg tw-font-medium tw-mb-4">Critères d'éligibilité</h3>

            <div className="tw-space-y-4">
              {/* Ces critères seront interactifs dans une future étape */}
              <div>
                <p className="tw-font-medium tw-text-gray-700">Le contrat est-il valide (non expiré) ?</p>
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-4">
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.isContractValid === true
                        ? 'tw-bg-green-100 tw-text-green-800 tw-border tw-border-green-300 tw-focus:bg-green-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ isContractValid: true })}
                  >
                    Oui
                  </button>
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.isContractValid === false
                        ? 'tw-bg-red-100 tw-text-red-800 tw-border tw-border-red-300 tw-focus:bg-red-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ isContractValid: false })}
                  >
                    Non
                  </button>
                </div>
              </div>

              <div>
                <p className="tw-font-medium tw-text-gray-700">La limite kilométrique est-elle respectée ?</p>
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-4">
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.isKilometrageLimitRespected === true
                        ? 'tw-bg-green-100 tw-text-green-800 tw-border tw-border-green-300 tw-focus:bg-green-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ isKilometrageLimitRespected: true })}
                  >
                    Oui
                  </button>
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.isKilometrageLimitRespected === false
                        ? 'tw-bg-red-100 tw-text-red-800 tw-border tw-border-red-300 tw-focus:bg-red-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ isKilometrageLimitRespected: false })}
                  >
                    Non
                  </button>
                </div>
              </div>

              <div>
                <p className="tw-font-medium tw-text-gray-700">Les délais de déclaration sont-ils respectés ?</p>
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-4">
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.isDeclarationTimeRespected === true
                        ? 'tw-bg-green-100 tw-text-green-800 tw-border tw-border-green-300 tw-focus:bg-green-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ isDeclarationTimeRespected: true })}
                  >
                    Oui
                  </button>
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.isDeclarationTimeRespected === false
                        ? 'tw-bg-red-100 tw-text-red-800 tw-border tw-border-red-300 tw-focus:bg-red-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ isDeclarationTimeRespected: false })}
                  >
                    Non
                  </button>
                </div>
              </div>

              <div>
                <p className="tw-font-medium tw-text-gray-700">Les pièces concernées sont-elles couvertes par la garantie ?</p>
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-4">
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.arePartsEligible === true
                        ? 'tw-bg-green-100 tw-text-green-800 tw-border tw-border-green-300 tw-focus:bg-green-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ arePartsEligible: true })}
                  >
                    Oui
                  </button>
                  <button
                    className={`tw-px-4 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none ${
                      qualificationData?.arePartsEligible === false
                        ? 'tw-bg-red-100 tw-text-red-800 tw-border tw-border-red-300 tw-focus:bg-red-100'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({ arePartsEligible: false })}
                  >
                    Non
                  </button>
                </div>
              </div>

              {/* Décision finale */}
              <div className="tw-mt-8">
                <h3 className="tw-text-lg tw-font-medium tw-mb-4">Décision de prise en charge</h3>
                <div className="tw-flex tw-flex-wrap tw-gap-4">
                  <button
                    className={`tw-px-5 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none tw-flex tw-items-center tw-gap-2 ${
                      qualificationData?.finalDecision === DecisionVoucherV2Type.PARTIAL_OR_TOTAL_AGREEMENT
                        ? 'tw-bg-green-600 tw-text-white tw-focus:bg-green-600'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({
                      finalDecision: DecisionVoucherV2Type.PARTIAL_OR_TOTAL_AGREEMENT
                    })}
                  >
                    <i className="fa-solid fa-check-circle"></i>
                    Accord (partiel ou total)
                  </button>
                  <button
                    className={`tw-px-5 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none tw-flex tw-items-center tw-gap-2 ${
                      qualificationData?.finalDecision === DecisionVoucherV2Type.COMMERCIAL_GESTURE
                        ? 'tw-bg-blue-600 tw-text-white tw-focus:bg-blue-600'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({
                      finalDecision: DecisionVoucherV2Type.COMMERCIAL_GESTURE
                    })}
                  >
                    <i className="fa-solid fa-gift"></i>
                    Geste commercial
                  </button>
                  <button
                    className={`tw-px-5 tw-py-2 tw-rounded-md tw-transition tw-focus:outline-none tw-flex tw-items-center tw-gap-2 ${
                      qualificationData?.finalDecision === DecisionVoucherV2Type.REFUSAL
                        ? 'tw-bg-red-600 tw-text-white tw-focus:bg-red-600'
                        : 'tw-bg-gray-100 tw-text-gray-800 tw-border tw-border-gray-200 tw-focus:bg-gray-100'
                    }`}
                    onClick={() => updateQualification({
                      finalDecision: DecisionVoucherV2Type.REFUSAL
                    })}
                  >
                    <i className="fa-solid fa-times-circle"></i>
                    Refus
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );

    case DecisionVoucherV2Step.CONFIGURATION:
      return (
        <div className="tw-space-y-4">
          <div className="tw-bg-green-50 tw-p-4 tw-rounded-lg tw-border tw-border-green-100">
            <p className="tw-text-green-700">
              Cette étape permet de configurer les détails de l'accord ou du refus.
              (Contenu à développer dans la phase suivante)
            </p>
          </div>
        </div>
      );

    case DecisionVoucherV2Step.VALIDATION:
      return (
        <div className="tw-space-y-4">
          <div className="tw-bg-purple-50 tw-p-4 tw-rounded-lg tw-border tw-border-purple-100">
            <p className="tw-text-purple-700">
              Cette étape permet de valider et finaliser la décision avant envoi.
              (Contenu à développer dans la phase suivante)
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

/**
 * Wrapper qui fournit le contexte de l'étape actuelle aux enfants
 */
interface StepContentWrapperProps {
  children: (props: { currentStep: DecisionVoucherV2Step }) => React.ReactNode;
}

const StepContentWrapper: React.FC<StepContentWrapperProps> = ({ children }) => {
  const { voucher, isLoading, error } = useDecisionVoucherContext();

  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-64">
        <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-t-2 tw-border-b-2 tw-border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-p-4 tw-bg-red-50 tw-text-red-700 tw-rounded-lg tw-border tw-border-red-200">
        <p className="tw-font-medium">Une erreur est survenue :</p>
        <p>{error.message}</p>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="tw-p-4 tw-bg-yellow-50 tw-text-yellow-700 tw-rounded-lg tw-border tw-border-yellow-200">
        <p>Pas de bon de décision disponible. Veuillez réessayer.</p>
      </div>
    );
  }

  return <>{children({ currentStep: voucher.step })}</>;
};

export default DecisionVoucherV2Page;